import { useState, useEffect } from "react";
import {
  Modal,
  Row,
  Col,
  Button,
  Typography,
  Card,
  Space,
  Tooltip,
  message,
  InputNumber,
  Tag,
  notification,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { IoCloseSharp } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { actions } from "../slice";
const { Title, Text } = Typography;

function AddBoxes({
  KitDetail,
  fetchKitDetail,
  setKitDataAfterAssetCreation,
  setIsOpenModalAfterAssetCreation,
  disabledActions,
}) {
  const dispatch = useDispatch();
  const [isAddBoxModalVisible, setIsAddBoxModalVisible] = useState(false);
  const [isBoxesAdding, setIsBoxesAdding] = useState(false);

  const [startBoxRange, setStartBoxRange] = useState("");
  const [endBoxRange, setEndBoxRange] = useState("");
  const [manualBox, setManualBox] = useState("");
  const [boxes, setBoxes] = useState([]);
  const [customPrefix, setCustomPrefix] = useState("");

  // Check if kit has existing boxes
  const hasExistingBoxes = KitDetail?.samplerBoxes?.length > 0;

  // Extract prefix from existing boxes or use custom prefix
  const prefix = hasExistingBoxes
    ? KitDetail?.samplerBoxes[0]?.boxId?.includes("-")
      ? KitDetail?.samplerBoxes[0]?.boxId.split("-").slice(0, -1).join("-")
      : ""
    : customPrefix.trim();

  // Generate Boxesssss
  const handleGenerateBoxes = () => {
    if (
      !startBoxRange ||
      !endBoxRange ||
      isNaN(startBoxRange) ||
      isNaN(endBoxRange) ||
      Number(startBoxRange) < 0 ||
      Number(endBoxRange) < 0
    ) {
      message.error("Please enter valid box numbers");
      return;
    }

    if (String(startBoxRange).length > 5 || String(endBoxRange).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }

    const start = parseInt(startBoxRange, 10);
    const end = parseInt(endBoxRange, 10);
    if (start > end) {
      message.error("Start should be less than end");
      return;
    }
    if (end - start > 99) {
      message.error("Maximum 100 Boxes can be generated at a time");
      return;
    }

    const newBoxes = [];
    for (let i = start; i <= end; i++) {
      const numericPart = String(i).padStart(5, "0");
      const box = prefix ? `${prefix}-${numericPart}` : numericPart;
      if (!boxes.includes(box)) {
        newBoxes.push(box);
      }
    }

    if (newBoxes?.length === 0) {
      message.info("No new unique boxes to add");
      return;
    }
    setBoxes([...boxes, ...newBoxes]);
  };

  // Add manual seal
  const handleAddManualBox = () => {
    if (!manualBox || isNaN(manualBox) || Number(manualBox) < 0) {
      message.error("Please enter valid box numbers");
      return;
    }

    if (String(manualBox).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }

    const numericPart = String(manualBox).padStart(5, "0");
    const newBox = prefix ? `${prefix}-${numericPart}` : numericPart;
    if (boxes.includes(newBox)) {
      message.warning("Box No. already exists");
      return;
    }
    setBoxes([...boxes, newBox]);
    setManualBox("");
  };

  // Delete seal
  const handleDeleteBox = (box) => {
    setBoxes(boxes.filter((s) => s !== box));
  };

  const handleAddBoxes = () => {
    if (boxes.length === 0) {
      message.error("Please generate or add at least one box");
      return;
    }

    const data = {
      sampleBoxes: boxes,
      samplerKitId: KitDetail._id,
    };
    setIsBoxesAdding(true);
    dispatch(
      actions.addBox({
        data,
        onSuccess: (data) => {
          setKitDataAfterAssetCreation(data.data);
          resetState();
          setIsOpenModalAfterAssetCreation(true);
          fetchKitDetail();
        },
        onFailure: (error) => {
          setIsBoxesAdding(false);

          let errorMessage = "Failed to Add Boxes";
          if (error?.data?.failedBoxNumbers?.length === boxes?.length) {
            errorMessage =
              "Oops, These box ids already taken, Please make unique ids";
          }
          notification.error({
            message: errorMessage || error.message,
            // description: error.message,
          });
        },
      })
    );
  };

  const resetState = () => {
    setIsAddBoxModalVisible(false);
    setStartBoxRange("");
    setEndBoxRange("");
    setManualBox("");
    setBoxes([]);
    setCustomPrefix("");
  };

  // Update box numbers when prefix changes (only when no existing boxes)
  useEffect(() => {
    if (hasExistingBoxes || boxes.length === 0) return;

    const updatedBoxes = boxes.map((box) => {
      const numericPart = box.includes("-") ? box.split("-").pop() : box;
      return customPrefix.trim()
        ? `${customPrefix.trim()}-${numericPart}`
        : numericPart;
    });

    setBoxes(updatedBoxes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPrefix, hasExistingBoxes]);

  return (
    <>
      <Tooltip
        title={
          disabledActions
            ? "Box can only be added when the kit is in the Pending or Packed stage"
            : "Add more boxes to this kit"
        }
      >
        <Button
          type="primary"
          onClick={() => {
            setIsAddBoxModalVisible(true);
          }}
          disabled={disabledActions}
        >
          Add More Boxes
        </Button>
      </Tooltip>
      <Modal
        visible={isAddBoxModalVisible}
        onCancel={resetState}
        title={
          <Row align="middle" justify="space-between" style={{ width: "100%" }}>
            <Title level={3} style={{ fontWeight: 800, margin: 0, padding: 0 }}>
              📦 Add More Boxes
            </Title>
            {!hasExistingBoxes && (
              <Space align="center">
                <Text style={{ fontWeight: 600, color: "#666", fontSize: 14 }}>
                  Prefix:
                </Text>
                <input
                  type="text"
                  value={customPrefix}
                  onChange={(e) =>
                    setCustomPrefix(e.target.value.toUpperCase())
                  }
                  placeholder="e.g., AG-BX"
                  style={{
                    width: 150,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: 14,
                    marginRight: 32,
                  }}
                />
              </Space>
            )}
          </Row>
        }
        footer={
          <Space>
            <Button
              key="cancel"
              onClick={resetState}
              style={{
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={isBoxesAdding}
              onClick={handleAddBoxes}
              disabled={boxes.length === 0}
              style={{
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              Add Boxes ({boxes.length})
            </Button>
          </Space>
        }
        width={800}
        bodyStyle={{ borderRadius: 16, padding: 24, background: "#fff" }}
        style={{ borderRadius: 16 }}
        closeIcon={<IoCloseSharp size="24" />}
        centered
        className="modal-content"
      >
        <div>
          <Text style={{ fontWeight: 600, color: "#232A36" }}>Box Range</Text>
          <Row gutter={8} style={{ marginTop: 6, marginBottom: 16 }}>
            <Col span={9}>
              <InputNumber
                value={startBoxRange}
                onChange={(value) => setStartBoxRange(value)}
                placeholder="Start"
                style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
                // size="large"
              />
            </Col>
            <Col span={9}>
              <InputNumber
                value={endBoxRange}
                onChange={(value) => setEndBoxRange(value)}
                placeholder="End"
                style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
                // size="large"
              />
            </Col>
            <Col span={6}>
              <Button
                type="primary"
                onClick={handleGenerateBoxes}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  fontWeight: 700,
                }}
              >
                Generate Boxes
              </Button>
            </Col>
          </Row>

          {/* Add Manual Seal */}
          <Text style={{ fontWeight: 600, color: "#232A36" }}>
            Add Manual Box
          </Text>
          <Row gutter={12} style={{ marginTop: 6, marginBottom: 15 }}>
            <Col span={18}>
              <InputNumber
                value={manualBox}
                onChange={(value) => setManualBox(value)}
                placeholder="Enter Numeric Value (e.g., 1)"
                style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
                // size="large"
              />
            </Col>
            <Col span={6}>
              <Button
                type="primary"
                // size="large"
                onClick={handleAddManualBox}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  fontWeight: 700,
                }}
              >
                Add Box
              </Button>
            </Col>
          </Row>

          {/* Box IDs */}
          {boxes?.length ? (
            <Card
              style={{
                borderRadius: 15,
                border: "1px solid #E5E8EC",
                background: "#FAFAFA",
              }}
            >
              <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: 12 }}
              >
                <Text
                  style={{ fontWeight: 700, fontSize: 16, color: "#232A36" }}
                >
                  Box IDs
                </Text>
                <Tag
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    background: "#E5E8EC",
                    borderRadius: 6,
                    padding: "4px 12px",
                    border: "none",
                  }}
                >
                  {boxes.length} {`Box${boxes.length > 1 ? "es" : ""}`}
                </Tag>
              </Row>
              <Row
                gutter={[12, 12]}
                style={{
                  border: "1px solid #E5E8EC",
                  padding: "12px",
                  borderRadius: 12,
                  overflow: "auto",
                  maxHeight: 220,
                }}
              >
                {boxes.map((box, idx) => (
                  <Col span={8} key={box + idx}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        background: "#fff",
                        borderRadius: 8,
                        border: "1px solid #E5E8EC",
                        padding: "4px 12px",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#232A36",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span>{box}</span>
                      <Space>
                        <Tooltip title="Delete">
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteBox(box)}
                            style={{ color: "#E14D4F", fontSize: 16 }}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

export default AddBoxes;
